import React, { useState, useEffect, useRef } from "react";
import {
  Segment,
  Header,
  Loader,
  Card,
  Image,
  Button,
  Label,
  Grid,
  Dropdown,
  Icon,
  Divider,
  Popup,
} from "semantic-ui-react";
import { circulationStatsApi } from "@api/stats";
import { sessionManager } from "@authentication/services/SessionManager";
import axios from "axios";

const useUser = () => {
  const [user, setUser] = useState(null);
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionManager.user) {
        setUser(sessionManager.user);
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);
  return user;
};

const TopBorrowedBooks = () => {
  const user = useUser();
  const [topBooks, setTopBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const savedMonth = localStorage.getItem("topBorrowedBooksMonth");
    return savedMonth
      ? savedMonth === "all"
        ? "all"
        : parseInt(savedMonth, 10)
      : "all";
  });

  const [selectedYear, setSelectedYear] = useState(() => {
    const savedYear = localStorage.getItem("topBorrowedBooksYear");
    return savedYear ? parseInt(savedYear, 10) : new Date().getFullYear();
  });

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [selectedRank, setSelectedRank] = useState(() => {
    const savedRank = localStorage.getItem("topBorrowedBooksRank");
    return savedRank ? parseInt(savedRank, 10) : 3;
  });

  //Dropdown options for ranking selection
  const rankOptions = [
    { key: 3, value: 3, text: "Top 3" },
    { key: 5, value: 5, text: "Top 5" },
    { key: 10, value: 10, text: "Top 10" },
    { key: 15, value: 15, text: "Top 15" },
  ];

  const monthOptions = [
    { key: "all", value: "all", text: "Default" },
    { key: 1, value: 1, text: "January" },
    { key: 2, value: 2, text: "February" },
    { key: 3, value: 3, text: "March" },
    { key: 4, value: 4, text: "April" },
    { key: 5, value: 5, text: "May" },
    { key: 6, value: 6, text: "June" },
    { key: 7, value: 7, text: "July" },
    { key: 8, value: 8, text: "August" },
    { key: 9, value: 9, text: "September" },
    { key: 10, value: 10, text: "October" },
    { key: 11, value: 11, text: "November" },
    { key: 12, value: 12, text: "December" },
  ];

  const academicYears = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];

  const buildDateRangeParams = (month, year) => {
    if (month === "all") {
      // For entire year: January 1st to December 31st
      return {
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      };
    } else {
      // For specific month: First day to last day of month
      const monthStr = month.toString().padStart(2, "0");
      const lastDay = new Date(year, month, 0).getDate(); // Get last day of month
      return {
        from: `${year}-${monthStr}-01`,
        to: `${year}-${monthStr}-${lastDay.toString().padStart(2, "0")}`,
      };
    }
  };

  const canEdit =
    user &&
    (Array.isArray(user.roles)
      ? user.roles.some((r) => ["admin", "librarian"].includes(r.toLowerCase()))
      : ["admin", "librarian"].includes(user?.roles?.toLowerCase?.()));

  // Main data fetching effect with proper cleanup
  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const fetchTopBooks = async () => {
      try {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError(null);

        // Build date range parameters
        const dateParams = buildDateRangeParams(selectedMonth, selectedYear);

        const response = await circulationStatsApi.getMostLoanedDocuments(
          dateParams.from,
          dateParams.to,
          { cancelToken: cancelTokenSource.token }
        );

        const sortedBooks = (response.data?.hits || [])
          .sort(
            (a, b) =>
              (b.metadata.loan_count || 0) - (a.metadata.loan_count || 0)
          )
          .slice(0, selectedRank);

        if (isMountedRef.current) {
          setTopBooks(sortedBooks);
          setLoading(false);
        }
      } catch (error) {
        if (!axios.isCancel(error) && isMountedRef.current) {
          console.error("Error fetching top borrowed books:", error);
          setError("Failed to load top borrowed books");
          setLoading(false);
        }
      }
    };

    fetchTopBooks();

    // Cleanup function - cancel request on unmount or dependency change
    return () => {
      cancelTokenSource.cancel("Component unmounted or filters changed");
    };
  }, [selectedMonth, selectedYear, selectedRank]);

  const updateFilters = (updates = {}) => {
    const newFilters = {
      rank: updates.rank !== undefined ? updates.rank : selectedRank,
      month: updates.month !== undefined ? updates.month : selectedMonth,
      year: updates.year !== undefined ? updates.year : selectedYear,
    };

    // Update state
    if (updates.rank !== undefined) setSelectedRank(newFilters.rank);
    if (updates.month !== undefined) setSelectedMonth(newFilters.month);
    if (updates.year !== undefined) setSelectedYear(newFilters.year);

    localStorage.setItem("topBorrowedBooksRank", newFilters.rank.toString());
    localStorage.setItem("topBorrowedBooksMonth", newFilters.month.toString());
    localStorage.setItem("topBorrowedBooksYear", newFilters.year.toString());

    // Close date picker if it was open
    if (updates.month !== undefined || updates.year !== undefined) {
      setIsDatePickerOpen(false);
    }
  };

  const handleRankingChange = (e, { value }) => {
    updateFilters({ rank: value });
  };

  const handleMonthChange = (e, { value }) => {
    updateFilters({ month: value });
  };

  const handleYearChange = (year) => {
    updateFilters({ year });
  };

  const getSelectedDateLabel = () => {
    if (selectedMonth === "all") {
      return ` ${selectedYear}`;
    }
    const monthName = monthOptions.find((m) => m.value === selectedMonth)?.text;
    return `${monthName} ${selectedYear}`;
  };

  const handleQuickDateSelect = (type) => {
    const now = new Date();

    if (type === "current-year") {
      updateFilters({
        month: "all",
        year: now.getFullYear(),
      });
    } else if (type === "current-month") {
      updateFilters({
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      });
    }
  };

  //Calculate dynamic items per row based on selected rank
  const getItemsPerRow = () => {
    if (selectedRank <= 3) return 3;
    if (selectedRank <= 5) return 5;
    if (selectedRank <= 10) return 5;
    return 4;
  };

  // Manual retry function
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Trigger re-fetch by updating a state that the useEffect depends on
    setSelectedRank((prev) => prev); // This will trigger the useEffect
  };

  // Date Picker Component
  const DatePickerPopup = () => (
    <div style={{ width: "340px", padding: "1em" }}>
      <Header as="h3" style={{ marginBottom: "1em" }}>
        Select Time Period
      </Header>

      {/* Year Selection */}
      <div style={{ marginBottom: "1em" }}>
        <label
          style={{
            fontSize: "0.9em",
            fontWeight: "bold",
            display: "block",
            marginBottom: "0.5em",
          }}
        >
          Academic Year:
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5em" }}>
          {academicYears.map((year) => (
            <Button
              key={year}
              size="small"
              basic={selectedYear !== year}
              color={selectedYear === year ? "blue" : undefined}
              onClick={() => handleYearChange(year)}
              style={{ minWidth: "60px" }}
              disabled={loading}
            >
              {year}
            </Button>
          ))}
        </div>
      </div>

      <Divider />

      {/* Month Selection */}
      <div style={{ marginBottom: "1em" }}>
        <label
          style={{
            fontSize: "0.9em",
            fontWeight: "bold",
            display: "block",
            marginBottom: "0.5em",
          }}
        >
          Month:
        </label>
        <Dropdown
          fluid
          selection
          value={selectedMonth}
          options={monthOptions}
          onChange={handleMonthChange}
          placeholder="Select Month"
          disabled={loading}
        />
      </div>

      {/* Quick Actions */}
      <div style={{ display: "flex", gap: "0.5em", marginTop: "1em" }}>
        <Button
          size="small"
          basic
          onClick={() => handleQuickDateSelect("current-year")}
          disabled={loading}
        >
          Current Year
        </Button>
        <Button
          size="small"
          basic
          onClick={() => handleQuickDateSelect("current-month")}
          disabled={loading}
        >
          This Month
        </Button>
      </div>

      {/* Current Selection Display */}
      <div
        style={{
          marginTop: "1em",
          padding: "0.5em",
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
          fontSize: "0.9em",
        }}
      >
        <Icon name="info circle" color="blue" />
        <strong>Selected:</strong> {getSelectedDateLabel()}
      </div>
    </div>
  );

  return (
    <Segment
      data-tour="top-borrowed-books"
      raised
      padded="very"
      style={{
        width: "100%",
        minHeight: "400px",
        maxHeight: "175vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "0.5em",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <Header
                  as="h2"
                  color="blue"
                  style={{ margin: 0, lineHeight: "1.2" }}
                >
                  {getSelectedDateLabel()} <br />
                  📚 Top {selectedRank} Borrowed Books
                </Header>
              </div>

              {canEdit && (
                <div
                  style={{
                    display: "flex",
                    gap: "0.5em",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {/* Date Picker */}
                  <Popup
                    trigger={
                      <Icon
                        name="calendar alternate"
                        size="large"
                        style={{
                          cursor: "pointer",
                          color: "grey",
                          marginTop: "0.5em",
                        }}
                      />
                    }
                    content={<DatePickerPopup />}
                    on="click"
                    position="bottom right"
                    wide="very"
                    open={isDatePickerOpen}
                    onOpen={() => setIsDatePickerOpen(true)}
                    onClose={() => setIsDatePickerOpen(false)}
                  />
                  <Dropdown
                    trigger={
                      <Icon
                        name="ellipsis vertical"
                        size="large"
                        style={{
                          cursor: "pointer",
                          color: "grey",
                          marginTop: "0.5em",
                          marginLeft: "1em",
                        }}
                      />
                    }
                    icon={null}
                    value={selectedRank}
                    options={rankOptions}
                    onChange={handleRankingChange}
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: "0.5em",
        }}
      >
        {loading ? (
          <Loader active inline="centered">
            Loading top borrowed books
          </Loader>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "2em" }}>
            <Icon name="exclamation triangle" size="big" color="red" />
            <p style={{ color: "red", marginTop: "1em" }}>{error}</p>
            <Button
              color="blue"
              onClick={handleRetry}
              style={{ marginTop: "1em" }}
            >
              Try Again
            </Button>
          </div>
        ) : topBooks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2em" }}>
            <Icon name="book" size="big" color="grey" />
            <p style={{ marginTop: "1em", color: "#666" }}>
              No borrowed books found for {getSelectedDateLabel()}
            </p>
            <Button
              basic
              onClick={() =>
                updateFilters({
                  month: "all",
                  year: new Date().getFullYear(),
                })
              }
              style={{ marginTop: "1em" }}
            >
              View All Time
            </Button>
          </div>
        ) : (
          <Card.Group centered stackable itemsPerRow={getItemsPerRow()}>
            {topBooks.map((book, index) => (
              <Card
                key={book.metadata?.pid || index}
                raised
                style={{
                  width: "180px",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <Label
                  color="orange"
                  ribbon
                  style={{
                    position: "absolute",
                    top: "10px",
                    left: "-10px",
                    zIndex: 1,
                  }}
                >
                  #{index + 1}
                </Label>

                {/* Image on top */}
                <Image
                  src={
                    book.metadata?.cover_metadata?.urls?.medium ||
                    `${process.env.PUBLIC_URL}/images/book-placeholder.png`
                  }
                  alt={book.metadata?.title || "Book Cover"}
                  style={{
                    width: "100%",
                    height: "200px",
                    objectFit: "cover",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                  }}
                  loading="lazy"
                />

                {/* Info below */}
                <Card.Content
                  style={{
                    padding: "0.8em",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Header
                    as="h4"
                    style={{
                      fontSize: "1em",
                      marginBottom: "0.4em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {book.metadata?.title
                      ? book.metadata.title.length > 20
                        ? book.metadata.title.slice(0, 20) + "..."
                        : book.metadata.title
                      : "Untitled"}
                  </Header>
                  <p style={{ fontSize: "0.8em", margin: "0.2em 0" }}>
                    <strong>Edition:</strong> {book.metadata?.edition || "N/A"}
                  </p>
                  <p style={{ fontSize: "0.8em", margin: "0.2em 0" }}>
                    <strong>Year:</strong>{" "}
                    {book.metadata?.publication_year || "N/A"}
                  </p>
                  <p style={{ fontSize: "0.8em", margin: "0.2em 0" }}>
                    <strong>Publisher:</strong>{" "}
                    {book.metadata?.imprint?.publisher || "Unknown"}
                  </p>
                  <p style={{ fontSize: "0.8em", margin: "0.2em 0" }}>
                    {book.metadata?.call_number || "No Call number specified"} |{" "}
                    {""}
                    {book.metadata?.sections || "No section specified"}
                  </p>
                  <p
                    style={{
                      fontWeight: "bold",
                      marginTop: "0.4em",
                      fontSize: "0.85em",
                    }}
                  >
                    {book.metadata?.loan_count ||
                      book.metadata?.circulation?.loans_count ||
                      0}{" "}
                    borrows
                  </p>
                </Card.Content>
              </Card>
            ))}
          </Card.Group>
        )}
      </div>
    </Segment>
  );
};

export default TopBorrowedBooks;
